import React from 'react';
import PropTypes from 'prop-types';
import {Formik, Form, FieldArray} from 'formik';
import {some, every, values, isEmpty} from 'lodash';
import {watchMedia, getMedia} from 'shared/mq';
import Text from 'shared/components/text';
import Modal from 'shared/components/modal';
import Checkbox from 'shared/components/checkbox';
import Pill from 'src/components/Pill';
import InfoTooltip from 'shared/components/info-tooltip';
import Loading from 'shared/components/loading';
import MediaQuery from 'shared/media-query';

export default class CollegeTaggingModal extends React.Component {
  static propTypes = {
    updateStudentCollegeTags: PropTypes.func.isRequired,
    show: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    collegeName: PropTypes.string,
    collegeNickName: PropTypes.string,
    initialTagIDs: PropTypes.array.isRequired,
    collegeTags: PropTypes.array.isRequired,
    onSuccess: PropTypes.func.isRequired,
    fetchModalData: PropTypes.func.isRequired,
    autoOpenConditions: PropTypes.object.isRequired,
  };

  componentDidMount() {
    this.mq = watchMedia(() => this.forceUpdate());

    this.props
      .fetchModalData()
      .then(() => every(values(this.props.autoOpenConditions)) && this.props.openModal());
  }

  validateTagLength = ({tags}) => (tags.length > 3 ? {tags: ['too-many-tags']} : {});

  isInitialValid = ({initialValues}) => !this.validateTagLength(initialValues).tags;

  selectedTags = (tagIDs) => this.props.collegeTags.filter(({id}) => tagIDs.includes(id));

  onSubmit = ({tags}, {setSubmitting, setStatus}) => {
    setSubmitting(true);
    this.props
      .updateStudentCollegeTags(tags)
      .then(() => setSubmitting(false))
      .then(this.props.onSuccess)
      .then(this.props.closeModal)
      .catch(() => {
        setStatus('error');
        window.setTimeout(() => setStatus('ok'), 3000);
      });
  };

  render() {
    const {show, closeModal, initialTagIDs, collegeTags, collegeNickName, collegeName} = this.props;
    const mq = getMedia();
    const explainer = `Tell us why you followed ${
      collegeNickName || collegeName
    }. Knowing what you value will help us connect you to the best-fit colleges.`;

    return (
      <Modal show={show} onClose={closeModal} className="college-tagging-modal">
        <Formik
          initialValues={{tags: initialTagIDs}}
          initialStatus="ok"
          validate={this.validateTagLength}
          isInitialValid={this.isInitialValid}
          onSubmit={this.onSubmit}
        >
          {({values: {tags}, errors, status}) => {
            const isValid = Object.keys(errors).length === 0;
            return (
              <Form>
                <FieldArray name="tags">
                  {({push, remove}) => {
                    const removeTag = (tagID) => () => remove(tags.indexOf(tagID));
                    const handleTagClick = (tagID) => (e) =>
                      e.target.checked ? push(tagID) : removeTag(tagID)();
                    const selectedTags = this.selectedTags(tags);
                    return (
                      <>
                        <Modal.TextHeader
                          onClose={closeModal}
                          subtitle={
                            <>
                              <p>
                                {mq.MS_AND_UP && explainer}
                                {mq.XS && collegeName}
                              </p>
                              <Text
                                color={isValid && status !== 'error' ? 'darkergrey' : 'red'}
                                className="college-tag-limit-instruction"
                              >
                                {status === 'ok'
                                  ? 'Select up to 3'
                                  : 'There was a problem, please try again.'}
                              </Text>
                              <MediaQuery.MS_AND_UP>
                                {selectedTags.map(({name, id}) => (
                                  <Pill iconRight="times-r" onClick={removeTag(id)} key={id}>
                                    {name}
                                  </Pill>
                                ))}
                              </MediaQuery.MS_AND_UP>
                            </>
                          }
                        >
                          What do you like about this college?
                          {mq.XS && (
                            <InfoTooltip className="college-tagging-tooltip">
                              {explainer}
                            </InfoTooltip>
                          )}
                        </Modal.TextHeader>

                        <Modal.Body>
                          {isEmpty(collegeTags) ? (
                            <Loading />
                          ) : (
                            <fieldset className="checkboxes">
                              {collegeTags.map(({name, id}) => (
                                <Checkbox
                                  key={id}
                                  name="tags"
                                  disabled={
                                    selectedTags.length === 3 && !some(selectedTags, {id: id})
                                  }
                                  value={id}
                                  checked={tags.includes(id)}
                                  label={name}
                                  onChange={handleTagClick(id)}
                                />
                              ))}
                            </fieldset>
                          )}
                        </Modal.Body>

                        <Modal.Footer
                          primaryButton={{
                            text: 'Save',
                            type: 'submit',
                            width: mq.XS ? '100%' : 130,
                            disabled: !isValid,
                          }}
                          secondaryButton={
                            mq.MS_AND_UP && {
                              text: 'Maybe later',
                              onClick: closeModal,
                              width: 130,
                              rightAligned: true,
                            }
                          }
                        />
                      </>
                    );
                  }}
                </FieldArray>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    );
  }
}
